import React, { useEffect, useState } from 'react'
import { searchDataSets } from '../../../services/APIServices'
import {
  Fleet,
  Extension,
  Map,
  dynamicDataExtensions,
  maxOptions
} from '../types'
import { useDispatch, useSelector } from 'react-redux'
import './DataSetGenerator.css'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import { getFileSize } from '../../../utils/helpers'
import DataSetsDisplay from '../DataSetsDisplay/DataSetsDisplay'
import FleetsPanel from '../FleetsPanel/FleetsPanel'
import ComponentBox from '../../../components/ComponentBox/ComponentBox'
import { Col, Container, ModalBody, Row } from 'reactstrap'
import HeaderBar from '../../../components/HeaderBar/HeaderBar'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import ICSDateBox from '../../../components/ICSDateBox/ICSDateBox'
import Joi from 'joi'
import { joiResolver } from '@hookform/resolvers/joi'
import { useForm } from 'react-hook-form'
import ICSTagBox from '../../../components/ICSTagBox/ICSTagBox'
import { Toolbar } from 'devextreme-react'
import { Item } from 'devextreme-react/toolbar'
import { ICSModal } from '../../../components'
import { DataSetForm } from './DataSetForm'
import { DisplayErrorState } from '../../../services/lib'
import { AppDispatch } from '../../../services/lib'
import { setEntityError } from '../../../redux/slices'
import { DefaultState } from '../../../types'
const schema = Joi.object({
  startdatetime: Joi.date().iso().allow('').allow(null).messages({
    'date.iso': `ISO date failure`,
    'string.min': `Must be a valid date 1`
  }),
  enddatetime: Joi.date()
    .iso()
    .allow('')
    .allow(null)
    .min(Joi.ref('startdatetime'))
    .messages({
      'iso.min': `Must be a valid date after 'Start Date'`
    }),
  collections: Joi.not().required().messages({
    'string.empty': `Required field`,
    'iso.min': `Must be a valid date after 'Start Date'`
  })
})

function DataSetGenerator(): JSX.Element {
  const dispatch: AppDispatch = useDispatch()
  const formName = 'resultsPage'
  const [resultJson, setResultJson] = useState([])
  const [resultJsonErrors, setResultJsonErrors] = useState<DisplayErrorState>({
    code: 'error',
    message: '',
    fields: []
  })
  const isDynamic = false
  const [dynamic] = useState<boolean>(isDynamic)
  const [max, setMax] = useState<number>(200)
  const [fleets, setFleets] = useState<Fleet[]>([])
  const [selectedCount, setSelectedCount] = useState<number>(0)
  const [collections, setCollections] = useState<{ name: string }[]>([])
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [resultFileData, setResultFileData] = useState({ count: 0, size: 0 })
  const [formDialogVisibility, setFormDialogVisibility] = useState(false)
  const [selectedVIds, setSelectedVids] = useState<number[]>([])
  const [selectedFIds, setSelectedFids] = useState<number[]>([])
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>([])
  const [filters, setFilters] = useState({})
  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    getValues
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema)
  })

  const formData = useSelector(
    (state: DefaultState) => state.forms && state.forms[formName]
  )
  const onSearchSubmit = () => {
    createFilters()
  }

  const createFilters = async () => {
    let start
    let end
    if (getValues().startdatetime) {
      start = getValues()
        .startdatetime.replaceAll('-', '/')
        .replaceAll('T', ' ')
        .replaceAll('Z', '')
    }
    if (getValues().enddatetime) {
      end = getValues()
        .enddatetime.replaceAll('-', '/')
        .replaceAll('T', ' ')
        .replaceAll('Z', '')
    }
    const filterObj = {
      fleetId: selectedFIds,
      vehicleId: selectedVIds,
      startDate: start,
      endDate: end,
      extensions: selectedExtensions,
      names: getValues().collections,
      sort: 'startDate',
      order: 'desc',
      offset: 0,
      max
    }
    setFilters(filterObj)
    const searchResults = await searchDataSets(filterObj, false)
    setResultJson(searchResults)
    compileFileDataDetail(searchResults)
  }
  function updateExtensions(extension: Extension) {
    extension.selected = !extension.selected
    setExtensions([...extensions])
    dynamicDataExtensions?.map((e: Extension) => {
      if (e.name === extension.name) {
        e.selected = extension.selected
      }
    })
    if (extension.selected) {
      setSelectedExtensions([...selectedExtensions, extension.name])
    } else {
      setSelectedExtensions(
        selectedExtensions.filter(function (value) {
          return value !== extension.name
        })
      )
    }
  }

  const compileFileDataDetail = (data) => {
    const fileDetails = data.reduce(
      (acc, result) => {
        const versionTotals = result?.versions?.reduce(
          (currentTotal, version) => {
            if (version.size) {
              return version.size + currentTotal
            }
            return currentTotal
          },
          0
        )

        return {
          count: result.versions.length + acc.count,
          size: versionTotals + acc.size
        }
      },
      { count: 0, size: 0 }
    )

    setResultFileData({ ...fileDetails, size: getFileSize(fileDetails.size) })
  }

  function onMaxLimitChange(value: string) {
    if (isNaN(parseInt(value))) setMax(200)
    setMax(parseInt(value))
  }

  useEffect(() => {
    if (formData) {
      Object.entries(formData).forEach((entry) => {
        setValue(entry[0], entry[1], {
          shouldDirty: true,
          shouldValidate: true
        })
      })
    }
  }, [formData, setValue, dispatch])

  return (
    <>
      <Container fluid={true}>
        <Row>
          <Col xs={12} className='wivi-col'>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Data Sets',
                  url: '/ui2/datasets'
                },
                { title: 'Add' }
              ]}
              help={{ title: 'Data Sets / Add', text: '' }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className='wivi-col'>
            <ComponentBox isCustom={true} cardClass='groups-results-container' cardBodyId='groupsCardBody' title="Search Criteria" accordion={true}>
              <Row>
                <Col xs={12} md={6}>
                  <FleetsPanel
                    vehicleIds={selectedVIds}
                    fleetIds={selectedFIds}
                    setVehicleId={setSelectedVids}
                    setFleetId={setSelectedFids}
                    fleets={fleets}
                    selectedFleets={{}}
                    selectedCount={selectedCount}
                    selectedVehicles={{}}
                    selectedExtensions={{}}
                    setFleets={setFleets}
                    setExtensions={setExtensions}
                    setCollections={setCollections}
                    setSelectedCount={setSelectedCount}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <div>
                    <div className="spacer">
                      <h4>Date Range</h4>
                      <ICSDateBox
                        name="startdatetime"
                        control={control}
                        errors={errors}
                        label="Start Date/Time"
                      />
                      <ICSDateBox
                        name="enddatetime"
                        control={control}
                        errors={errors}
                        label="End Date/Time"
                      />
                    </div>
                    <div className="spacer">
                      <ICSTagBox
                        name="collections"
                        label="Collections"
                        control={control}
                        errors={errors}
                        dataList={collections.map((collection) => {
                          return collection.name
                        })}
                      />
                    </div>
                    <div className="spacer">
                      <h4>File Extensions</h4>
                      {!extensions.length && (
                        <p>
                          No file extensions for the currently selected Fleets
                          or Vehicles
                        </p>
                      )}
                      {extensions?.map((extension: Extension) => (
                        <button
                          type="button"
                          key={extension.name}
                          className={`extension-button ${
                            extension.selected ? 'active' : ''
                          }`}
                          onClick={() => updateExtensions(extension)}
                        >
                          {extension.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
              <Toolbar>
                <Item
                  widget="dxButton"
                  location="after"
                  render={() => {
                    return (
                      <select
                        id="limit"
                        className="max-limit"
                        onChange={(e) => onMaxLimitChange(e.target.value)}
                      >
                        {maxOptions?.map((item) => (
                          <option key={item} value={item}>
                            {item} results
                          </option>
                        ))}
                        ;
                      </select>
                    )
                  }}
                />
                <Item
                  widget="dxButton"
                  location="after"
                  render={() => {
                    return (
                      <ICSButton
                        id="searchButton"
                        icon="search"
                        text="Search"
                        type="success"
                        onClick={handleSubmit(onSearchSubmit)}
                        disabled={selectedCount === 0}
                      />
                    )
                  }}
                />
              </Toolbar>
            </ComponentBox>
          </Col>
        </Row>
        <Row>
          <Col className='wivi-col'>
            <ComponentBox isCustom={true} cardClass='groups-results-container' cardBodyId='groupsCardBody'>
              <DataSetsDisplay
                resultJson={resultJson}
                setResultJson={setResultJson}
                dynamic={dynamic}
                extensions={extensions}
                resultJsonErrors={resultJsonErrors}
              />
            </ComponentBox>
          </Col>
        </Row>
      </Container>
      <ICSModal
        visible={formDialogVisibility}
        onHiding={() => {
          setFormDialogVisibility(false)
          dispatch(
            setEntityError({
              sliceName: 'datasets',
              entityName: 'Dataset',
              error: null
            })
          )
        }}
        title="Save Data Set"
        dragEnabled={false}
        closeOnOutsideClick={true}
      >
        <ModalBody>
          <DataSetForm
            hideModal={() => null}
            selectedTotal={resultFileData}
            location={false}
            filters={filters}
          ></DataSetForm>
        </ModalBody>
      </ICSModal>
    </>
  )
}
export default DataSetGenerator
